import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save, Upload, FileText } from 'lucide-react';
import { Section, Field, Modal, ImageUpload } from './_shared';

const empty = { title: '', description: '', pdf_base64: '', pdf_url: '', cover_base64: '', order: 0 };

export default function CasePanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { const r = await cx.get('/case-studies'); setItems(r.data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const onPickPdf = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('PDF too large (10MB max)'); return; }
    const data = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
    setEditing({ ...editing, pdf_base64: data });
  };

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at;
      if (editing.id) await cx.put(`/case-studies/${editing.id}`, payload);
      else await cx.post('/case-studies', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try { await cx.delete(`/case-studies/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Section title="Case Studies (PDFs)" action={<button data-testid="admin-case-add" onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus size={14} /> Add case study</button>}>
      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : items.length === 0 ? <p className="text-muted text-sm">No case studies yet.</p> : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`admin-case-row-${c.id}`} className="border border-subtle surface rounded-md p-4 flex gap-4 items-start">
              <div className="w-20 h-20 rounded-md overflow-hidden surface-2 shrink-0 flex items-center justify-center">
                {c.cover_base64 ? <img src={c.cover_base64} className="photo-fit" alt="" /> : <FileText size={20} className="text-muted" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-prim">{c.title}</p>
                {c.description && <p className="text-sec text-sm mt-1 line-clamp-2">{c.description}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(c)} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(c.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit case study' : 'New case study'}>
          <div className="space-y-4">
            <ImageUpload value={editing.cover_base64} onChange={(v) => setEditing({ ...editing, cover_base64: v })} label="Cover image" />
            <Field label="Title"><input className="lux-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Description"><textarea rows={3} className="lux-input resize-none" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            <Field label="PDF — upload (max 10MB)">
              <label className="btn-secondary text-xs cursor-pointer inline-flex"><Upload size={12} /> {editing.pdf_base64 ? 'Replace PDF' : 'Upload PDF'}
                <input type="file" accept="application/pdf" onChange={onPickPdf} className="hidden" />
              </label>
              {editing.pdf_base64 && <p className="text-xs text-muted mt-2">PDF attached ({Math.round(editing.pdf_base64.length/1024)} KB encoded)</p>}
            </Field>
            <Field label="… or external PDF URL"><input className="lux-input" placeholder="https://…" value={editing.pdf_url} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} /></Field>
            <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-case-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
